<template lang='pug'>
  div
    bionic-metrics-page-loading(v-if='loading')
    v-container(v-if='account' v-show='!loading')
      v-card.mb-4
        v-card-title.text-h5.font-weight-light Account Information
        v-card-text
          v-text-field(prepend-icon='person' label='Account Name' v-model='name' required type='text')
          v-text-field(prepend-icon='email' label='Email' v-model='email' required type='email')
          v-text-field(prepend-icon='phone' label='Phone' v-model='phone' type='phone')
          v-text-field(prepend-icon='home' label='Address' v-model='address' type='text')
          v-text-field(prepend-icon='location_city' label='City' v-model='city' type='text')
          v-select(prepend-icon='map' label='State' :items='states' v-model='state' single-line auto hide-details).mb-4
          v-select(prepend-icon='flag' label='Country' :items='countries' v-model='country' single-line auto hide-details).mb-4
          v-text-field(prepend-icon='markunread_mailbox' label='Postal Code' v-model='zipCode' type='text')
      v-card(v-if='account && user.accountAdmin' :loading='subscriptionUpdating').mb-4
        v-card-title.text-h5.font-weight-light Subscription Information
        v-card-text
          v-row
            v-col(cols='3').text-body-2
              span(v-if='account') Subscription
            v-col {{subscription}}
          v-row
            v-col(cols='3').text-body-2 Credit Card
            v-col
              span {{ccInfo}}
              | &nbsp;
              a.edit-link(@click='showCCDialog = true')
                | Update Credit Card
                v-icon(small) edit
      v-card#users.mb-4
        v-card-title.text-h5.font-weight-light Users
          v-btn(color='primary' @click='showInviteUserDialog = true' text icon)
            v-icon add
        table-list(:headers='userTable.headers'
          :items='users'
          v-model='selected'
          item-key='email'
          table-only
          no-pagination
          @update='updateUsers'
          :total='users.length')
          template(v-slot:row='{item}')
            td {{ item.email }}
            td {{ item.firstName}}
            td {{ item.lastName}}
            td
              v-icon(v-if='item.accountAdmin') check
            td
              v-icon(v-if='item.enabled') check
        v-card-actions
          v-tooltip(right v-if='selected.length')
            template(v-slot:activator='{on}')
              v-btn(text v-on='on' @click='deleteSelected()' color='error')
                | Remove User
            span Remove {{_.map(selected, 'email').join(', ')}}
          v-spacer
          v-btn(text color='primary' @click='showInviteUserDialog = true') Invite User
      div.space
      credit-card(v-model='showCCDialog' @token='updateCard')
      v-dialog(v-model='showInviteUserDialog' max-width='500px')
        v-card
          form(@submit.prevent='inviteUser')
            v-card-title
              span.text-h5.font-weight-light Invite User
            v-card-text
              v-container(grid-list-md)
                v-row(wrap)
                  v-col(cols='12')
                    v-text-field(type='email' label='Email' v-model='invitedUser.email' required)
                  v-col(cols='12')
                    v-checkbox(label='Administrator' v-model='invitedUser.accountAdmin')
            v-card-actions
              v-spacer
              v-btn(text @click.native='showInviteUserDialog=false') Cancel
              v-btn(text type='submit' color='primary' :loading='inviting') Invite

</template>

<script>
import _ from 'lodash';
import {mapMutations, mapGetters, mapActions} from 'vuex';
import {createHelpers} from 'vuex-map-fields';
import Notifications from '../../services/notification.service';
import Account from '../../api/account';
import countries from '../../services/country.service';
import TableList from '../../components/TableList';
import SaveMixin from '../../mixins/save.mixin';
import BionicMetricsPageLoading from '../../components/PageLoading';
import CreditCard from '../../components/CreditCard';

// We're using the same getter and mutation types
// as we've used in the store above.
const {mapFields} = createHelpers({
  getterType: 'getAccountField',
  mutationType: 'updateAccountField'
});

export default {
  name: 'account',
  components: {CreditCard, BionicMetricsPageLoading, TableList},
  mixins: [SaveMixin],
  mounted() {
    this.load();
  },
  computed: {
    _() {
      return _;
    },
    subscription() {
      return 'Bionic Metrics Enterprise';
    },
    ccInfo() {
      if (!this.account) return '';
      if (!this.account.creditCard) return '';
      const {cardType, last4} = this.account.creditCard;
      if (!cardType || !last4) return '';
      return `${cardType} ending in ${last4}`;
    },
    ...mapGetters({
      dirty: 'isAccountDirty',
      account: 'getAccount',
      users: 'getAccountUsers',
      user: 'getUser',
      planPro: 'getPlanPro',
      planEssentials: 'getPlanEssentials'
    }),
    ...mapFields({
      name: 'account.name',
      email: 'account.email',
      phone: 'account.phone',
      address: 'account.address',
      city: 'account.city',
      state: 'account.state',
      country: 'account.country',
      zipCode: 'account.zipCode'
    })
  },
  methods: {
    async updateUsers() {
      await this.getAccountUsers();
    },
    async load() {
      this.loading = true;
      await this.getAccount(this.user);
      await this.getAccountUsers();
      this.loading = false;
    },
    async updateCard(result) {
      try {
        this.subscriptionUpdating = true;
        const {token} = result;
        const {card} = token;
        const body = {
          source: token.id,
          last4: card.last4,
          cardType: card.brand,
          email: this.account.email
        };
        if (this.account.stripeCustomerId) {
          await Account.updatePaymentSource(this.account._id, body);
        } else {
          await Account.createCustomer(this.account._id, body);
        }
        //Finally, refresh.
        await this.getAccount(this.user);
        await Notifications.toast('Payment Information Updated');
        this.showCCDialog = false;
      } catch (error) {
        console.error(error);
        await Notifications.alert('An error occurred saving payment information. Check console logs for details. Please contact support');
      } finally {
        this.subscriptionUpdating = false;
      }
    },
    async save() {
      try {
        await this.updateAccount();
        return Notifications.toast('Saved Account Information');
      } catch (e) {
        console.error(e);
        return Notifications.errorToast('Unable to save Account Information', {
          duration: 100000,
          retry: this.save
        });
      }
    },
    async inviteUser() {
      try {
        this.inviting = true;
        await Account.inviteUser(this.account._id, this.invitedUser);
        Notifications.toast(`${this.invitedUser.email} has been invited`);
        this.showInviteUserDialog = false;
        this.invitedUser = {};
        await this.getAccountUsers(); // refresh user list.
      } catch (error) {
        if (_.get(error, 'response.data.message') === 'EMAIL_TAKEN') {
          Notifications.alert(`<strong>${this.invitedUser.email}</strong> already has an active account. Please use a different email address to invite them to this account.`);
        } else {
          return Notifications.alert('Unable to invite user to account. Please contact support.');
        }
      } finally {
        this.inviting = false;
      }
    },
    async deleteSelected() {
      const emails = _.map(this.selected, 'email').join(', ');
      if (!(await Notifications.confirm('Remove', `Are you sure you want to remove the following users from this account: <strong>${emails}</strong>?`))) return;

      const all = [];
      _.each(this.selected, (user) => {
        if (user._id === this.user._id) return; // Can't remove yourself
        all.push(this.removeUserFromAccount({accountId: this.user.account._id, userId: user._id}));
      });
      await Promise.all(all);
      this.selected = [];
      Notifications.toast(`User${all.length > 1 ? 's' : ''} Removed From Account`);
    },
    ...mapActions({
      removeUserFromAccount: 'removeUserFromAccount',
      updateAccount: 'updateAccount',
      getAccount: 'getAccount',
      getAccountUsers: 'getAccountUsers',
      getPublishKey: 'getPublishKey'
    }),
    ...mapMutations({clearDirty: 'clearAccountDirty'})
  },
  data() {
    return {
      loading: false,
      inviting: false,
      selected: [],
      userTable: {
        headers: [
          {text: 'Email', align: 'left', sortable: false, value: 'email'},
          {text: 'First Name', align: 'left', sortable: false, value: 'firstName'},
          {text: 'Last Name', align: 'left', sortable: false, value: 'lastName'},
          {text: 'Account Administrator', align: 'left', sortable: false, value: 'accountAdmin'},
          {text: 'Active', align: 'left', sortable: false, value: 'enabled'}
        ]
      },
      showInviteUserDialog: false,
      showCCDialog: false,
      subscriptionUpdating: false,
      invitedUser: {
        email: null,
        accountAdmin: false
      },
      states: [
        'Alabama', 'Alaska', 'American Samoa', 'Arizona',
        'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'District of Columbia', 'Federated States of Micronesia',
        'Florida', 'Georgia', 'Guam', 'Hawaii', 'Idaho',
        'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
        'Louisiana', 'Maine', 'Marshall Islands', 'Maryland',
        'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
        'Missouri', 'Montana', 'Nebraska', 'Nevada',
        'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
        'North Carolina', 'North Dakota', 'Northern Mariana Islands', 'Ohio',
        'Oklahoma', 'Oregon', 'Palau', 'Pennsylvania', 'Puerto Rico',
        'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee',
        'Texas', 'Utah', 'Vermont', 'Virgin Island', 'Virginia',
        'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
      ],
      countries: _.map(countries, 'name'),
      cardElementStyle: {
        base: {
          color: '#32325d',
          lineHeight: '18px',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      }
    };
  }
};
</script>

<style scoped>
  .space {
    min-height: 75vh;
  }
</style>
